<template>
  <div data-test-id="medicalClearanceForm">
    <v-row>
      <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
        <div style="position: relative">
          <DatePicker
            required
            v-model="clearanceDate"
            :label="t('fields.clearance.clearanceDate')"
            @update:model-value="updateExpiration"
          />
          <StatusChip
            v-if="!settingsStore.globals.clearance.expires"
            style="position: absolute; top: 1.05rem; right: 0"
            :iso-date="expiration?.toISOString()"
            :scheduled="peopleStore.selectedItem!.clearanceAppointments > 0"
            :days-until-due="settingsStore.globals.clearance.daysUntilDue"
            :msg-compliant="t('labels.cleared')"
            :msg-no-record="t('labels.notCleared')"
            :expires="false"
            :mini="true"
          />
        </div>
        <div v-if="settingsStore.globals.clearance.expires" style="position: relative">
          <DatePicker v-model="expiration" :label="t('fields.clearance.expiration')" />
          <StatusChip
            style="position: absolute; top: 1.05rem; right: 0"
            :iso-date="expiration?.toISOString()"
            :scheduled="peopleStore.selectedItem!.clearanceAppointments > 0"
            :days-until-due="settingsStore.globals.clearance.daysUntilDue"
            :msg-compliant="t('labels.cleared')"
            :msg-no-record="t('labels.notCleared')"
            :expires="settingsStore.globals.clearance.expires"
            :mini="true"
          />
        </div>

        <PrimaryTextArea
          :maxlength="255"
          v-model="item.restrictions"
          :label="t('fields.clearance.restrictions')"
        />
        <StatusChip
          v-if="settingsStore.globals.clearance.enabled"
          :scheduled="peopleStore.selectedItem!.clearanceAppointments > 0"
          :label="t('fields.clearance.status')"
          :iso-date="expiration?.toISOString()"
          :days-until-due="settingsStore.globals.clearance.daysUntilDue"
          :msg-compliant="t('labels.cleared')"
          :msg-no-record="t('labels.notCleared')"
          :expires="settingsStore.globals.clearance.expires"
        />
      </v-col>
      <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6" xxl="6">
        <p>
          <strong>{{ t('labels.authMedicalClearance') }}</strong
          ><br />
          <em style="font-size: 0.75rem">{{ t('labels.authMedicalClearanceDetail') }}</em>
        </p>
        <PrimarySwitch :label="t('fields.clearance.disposable')" v-model="item.disposable" />
        <PrimarySwitch
          density="compact"
          :label="t('fields.clearance.hfNPR')"
          v-model="item.hfNPR"
          color="primary"
        />
        <PrimarySwitch :label="t('fields.clearance.ffNPR')" v-model="item.ffNPR" />
        <PrimarySwitch :label="t('fields.clearance.hfPAPR')" v-model="item.hfPAPR" />
        <PrimarySwitch :label="t('fields.clearance.ffPAPR')" v-model="item.ffPAPR" />
        <PrimarySwitch :label="t('fields.clearance.hfSAR')" v-model="item.hfSAR" />
        <PrimarySwitch :label="t('fields.clearance.ffSAR')" v-model="item.ffSAR" />
        <PrimarySwitch :label="t('fields.clearance.scba')" v-model="item.scba" />
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { isDefined, replaceTime } from '@/composables/utils'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IDatasetStore } from '@/interfaces/api/IDatasetStore'
import type { IMedicalClearancesStore } from '@/interfaces/api/IMedicalClearancesStore'
import type { IPeopleStore } from '@/interfaces/api/IPeopleStore'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import dayjs from 'dayjs'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const datasetsStore = TableStoreFactory.get(TableStoreId.Datasets) as IDatasetStore
const peopleStore = TableStoreFactory.get(TableStoreId.People) as IPeopleStore
const store = TableStoreFactory.get(TableStoreId.MedicalClearances) as IMedicalClearancesStore
const settingsStore = useSettingsStore()
const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS

// REACTIVE VARIABLES

// COMPUTED PROPERTIES
const clearanceDate = computed({
  get: (): Date | null => {
    if (isDefined(item.value.clearanceDate)) {
      return new Date(item.value.clearanceDate!)
    }
    return null
  },
  set: (value: Date | null) => {
    if (value) {
      item.value.clearanceDate = replaceTime(value, datasetsStore.activeDataset.timeZone)
    } else {
      item.value.clearanceDate = null
    }
  }
})

const expiration = computed({
  get: (): Date | null => {
    if (isDefined(item.value.expiration)) {
      return new Date(item.value.expiration!)
    }
    return null
  },
  set: (value: Date | null) => {
    if (value) {
      item.value.expiration = replaceTime(value, datasetsStore.activeDataset.timeZone)
    } else {
      item.value.expiration = null
    }
  }
})

const item = computed(() => {
  if (store.selectedItem) {
    return store.selectedItem
  }
  return {}
})

// LIFECYCLE HOOKS

// WATCHERS

// FUNCTIONS
const updateExpiration = () => {
  if (clearanceDate.value) {
    expiration.value = dayjs(clearanceDate.value)
      .add(settingsStore.globals.clearance.daysToExpire, 'days')
      .toDate()
  } else {
    expiration.value = null
  }
}
</script>
